import React, { useRef, useEffect } from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { useLanguage } from '../contexts/LanguageContext';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const RadarChart = ({ 
  data, 
  title, 
  type = 'individual', // 'individual', 'company', 'comparison'
  width = 400, 
  height = 400,
  className = "" 
}) => {
  const { t } = useLanguage();
  const chartRef = useRef(null);

  // Configuration based on the circular design from the PDF
  const getChartConfig = () => {
    const baseConfig = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: !!title,
          text: title || '',
          font: {
            size: 14,
            weight: 'bold'
          },
          padding: title ? 10 : 0
        },
        legend: {
          position: 'bottom',
          labels: {
            padding: 10,
            font: {
              size: 10
            },
            usePointStyle: true,
            pointStyle: 'circle'
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: '#fff',
          borderWidth: 1,
          callbacks: {
            label: function(context) {
              const label = context.dataset.label || '';
              const value = context.raw;
              return `${label}: ${value} ${t('charts.tokens', 'tokens')}`;
            }
          }
        }
      },
      scales: {
        r: {
          beginAtZero: true,
          min: 0,
          max: 25, // Based on the token distribution system
          ticks: {
            stepSize: 5,
            font: {
              size: 10
            },
            color: '#666',
            backdropColor: 'rgba(255, 255, 255, 0.8)',
            backdropPadding: 2
          },
          grid: {
            color: 'rgba(0, 0, 0, 0.1)',
            lineWidth: 1
          },
          angleLines: {
            color: 'rgba(0, 0, 0, 0.1)',
            lineWidth: 1
          },
          pointLabels: {
            font: {
              size: 11,
              weight: 'bold'
            },
            color: '#333',
            padding: 15
          }
        }
      },
      elements: {
        line: {
          borderWidth: 2,
          borderCapStyle: 'round',
          borderJoinStyle: 'round'
        },
        point: {
          radius: 3,
          hoverRadius: 5,
          borderWidth: 0,
          hoverBorderWidth: 0
        }
      }
    };

    // Adjust configuration based on chart type
    if (type === 'comparison') {
      baseConfig.scales.r.max = 30; // Higher max for company comparisons
    }

    return baseConfig;
  };

  // Enhanced color schemes - Homologado a Azul y Rojo sin contorno
  const getColorSchemes = () => {
    // Colores homologados: Azul para ofertas/primario, Rojo para necesidades/secundario
    // Más transparentes y claros
    const blueColor = 'rgba(59, 130, 246, 0.6)'; // Azul semi-transparente
    const blueBg = 'rgba(59, 130, 246, 0.08)';
    const bluePoint = 'rgba(59, 130, 246, 0.5)'; // Puntos más claros
    const redColor = 'rgba(239, 68, 68, 0.6)'; // Rojo semi-transparente
    const redBg = 'rgba(239, 68, 68, 0.08)';
    const redPoint = 'rgba(239, 68, 68, 0.5)'; // Puntos más claros
    
    return {
      primary: {
        backgroundColor: blueBg,
        borderColor: blueColor,
        pointBackgroundColor: bluePoint,
        pointBorderColor: bluePoint,
        pointHoverBackgroundColor: blueColor,
        pointHoverBorderColor: blueColor
      },
      secondary: {
        backgroundColor: redBg,
        borderColor: redColor,
        pointBackgroundColor: redPoint,
        pointBorderColor: redPoint,
        pointHoverBackgroundColor: redColor,
        pointHoverBorderColor: redColor
      },
      employee: {
        backgroundColor: blueBg,
        borderColor: blueColor,
        pointBackgroundColor: bluePoint,
        pointBorderColor: bluePoint,
        pointHoverBackgroundColor: blueColor,
        pointHoverBorderColor: blueColor
      },
      company: {
        backgroundColor: redBg,
        borderColor: redColor,
        pointBackgroundColor: redPoint,
        pointBorderColor: redPoint,
        pointHoverBackgroundColor: redColor,
        pointHoverBorderColor: redColor
      }
    };
  };

  // Validate data before processing
  if (!data || !data.datasets || !Array.isArray(data.datasets) || data.datasets.length === 0) {
    return (
      <div className={`relative ${className} flex items-center justify-center`} style={{ width, height }}>
        <p className="text-gray-500 text-sm">No data available for chart</p>
      </div>
    );
  }

  // Apply color schemes to data - ALWAYS use homologated colors (blue/red)
  const processedData = {
    ...data,
    datasets: data.datasets.map((dataset, index) => {
      const colors = getColorSchemes();
      let colorScheme;

      if (type === 'comparison') {
        colorScheme = index === 0 ? colors.employee : colors.company;
      } else {
        colorScheme = index === 0 ? colors.primary : colors.secondary;
      }

      return {
        ...dataset,
        ...colorScheme,
        tension: 0.1, // Smooth lines
      };
    })
  };

  return (
    <div className={`relative ${className}`} style={{ width, height }}>
      <Radar
        ref={chartRef}
        data={processedData}
        options={getChartConfig()}
      />
    </div>
  );
};

// Individual Assessment Radar Chart
const IndividualRadarChart = ({ assessmentData, className = "", compact = false }) => {
  const { t } = useLanguage();

  if (!assessmentData || !assessmentData.radar_chart) {
    return (
      <div className={`flex items-center justify-center bg-gray-50 rounded-lg ${className}`} style={{ minHeight: compact ? 200 : 400 }}>
        <p className="text-gray-500">
          {t('charts.no_data_available', 'No data available')}
        </p>
      </div>
    );
  }

  // Compact mode: medium chart, no scores, no title
  if (compact) {
    return (
      <RadarChart
        data={assessmentData.radar_chart}
        title=""
        type="individual"
        width={320}
        height={300}
        className={className}
      />
    );
  }

  return (
    <div className="space-y-4">
      <RadarChart
        data={assessmentData.radar_chart}
        title={t('assessment_ui.individual_dimensions_profile', 'Individual Dimensions Profile')}
        type="individual"
        width={500}
        height={400}
        className={className}
      />
      
      {/* Scores Display */}
      {assessmentData.scores && (
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div className="bg-blue-50 p-3 rounded-lg text-center">
            <h4 className="text-sm font-semibold text-blue-900">
              {t('charts.compatibility', 'Compatibility')}
            </h4>
            <p className="text-xl font-bold text-blue-600">
              {Math.round(assessmentData.scores.compatibilidad)}%
            </p>
          </div>
          <div className="bg-green-50 p-3 rounded-lg text-center">
            <h4 className="text-sm font-semibold text-green-900">
              {t('charts.balance', 'Balance')}
            </h4>
            <p className="text-xl font-bold text-green-600">
              {Math.round(assessmentData.scores.balance)}%
            </p>
          </div>
          <div className="bg-purple-50 p-3 rounded-lg text-center">
            <h4 className="text-sm font-semibold text-purple-900">
              {t('charts.growth', 'Growth')}
            </h4>
            <p className="text-xl font-bold text-purple-600">
              {Math.round(assessmentData.scores.potencial_crecimiento)}%
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Company Assessment Radar Chart
const CompanyRadarChart = ({ companyData, className = "", showTitle = false, showCompanyInfo = false, height = 320 }) => {
  const { t } = useLanguage();

  if (!companyData || !companyData.radar_chart) {
    return (
      <div className={`flex items-center justify-center bg-gray-50 rounded-lg ${className}`} style={{ minHeight: 280 }}>
        <p className="text-gray-500">
          {t('charts.no_data_available', 'No data available')}
        </p>
      </div>
    );
  }

  // Additional validation for radar_chart structure
  if (!companyData.radar_chart.datasets || !Array.isArray(companyData.radar_chart.datasets) || companyData.radar_chart.datasets.length === 0) {
    return (
      <div className={`flex items-center justify-center bg-gray-50 rounded-lg ${className}`} style={{ minHeight: 280 }}>
        <p className="text-gray-500">
          {t('charts.no_data_available', 'No data available')}
        </p>
      </div>
    );
  }

  return (
    <div>
      <RadarChart
        data={companyData.radar_chart}
        title={showTitle ? t('assessment_ui.company_dimensions_profile', 'Company Dimensions Profile') : ''}
        type="company"
        width={380}
        height={height}
        className={className}
      />
      
      {/* Company Info - optional */}
      {showCompanyInfo && companyData.company_info && (
        <div className="bg-gray-50 p-4 rounded-lg mt-2">
          <h4 className="font-semibold text-gray-900 mb-2">
            {companyData.company_info.nombre}
          </h4>
          <p className="text-sm text-gray-600">
            {t('charts.based_on', 'Based on')} {companyData.company_info.total_assessments} {t('charts.completed_assessments', 'completed assessments')}
          </p>
        </div>
      )}
    </div>
  );
};

// Comparison Radar Chart
const ComparisonRadarChart = ({ employeeData, companyData, className = "" }) => {
  const { t } = useLanguage();

  if (!employeeData?.radar_chart || !companyData?.radar_chart) {
    return (
      <div className={`flex items-center justify-center bg-gray-50 rounded-lg ${className}`} style={{ minHeight: 400 }}>
        <p className="text-gray-500">
          {t('charts.insufficient_data', 'Insufficient data for comparison')}
        </p>
      </div>
    );
  }

  // Combine datasets for comparison
  const comparisonData = {
    labels: employeeData.radar_chart.labels,
    datasets: [
      {
        ...employeeData.radar_chart.datasets[0], // Ofertas
        label: t('charts.employee_offers', 'Employee - Offers')
      },
      {
        ...companyData.radar_chart.datasets[0], // Company average ofertas
        label: t('charts.company_average', 'Company - Average')
      }
    ]
  };

  return (
    <div className="space-y-4">
      <RadarChart
        data={comparisonData}
        title={t('charts.employee_vs_company', 'Employee vs Company Comparison')}
        type="comparison"
        width={600}
        height={450}
        className={className}
      />
      
      {/* Comparison Insights */}
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">
            {t('charts.employee_profile', 'Employee Profile')}
          </h4>
          {employeeData.assessment_info && (
            <div className="space-y-1 text-sm text-blue-700">
              <p><strong>{t('charts.name', 'Name:')}:</strong> {employeeData.assessment_info.usuario_nombre}</p>
              <p><strong>{t('charts.company', 'Company:')}:</strong> {employeeData.assessment_info.empresa_nombre}</p>
              <p><strong>{t('charts.type', 'Type:')}:</strong> {employeeData.assessment_info.tipo}</p>
            </div>
          )}
        </div>
        
        <div className="bg-red-50 p-4 rounded-lg">
          <h4 className="font-semibold text-red-900 mb-2">
            {t('charts.company_average', 'Company Average')}
          </h4>
          {companyData.company_info && (
            <div className="space-y-1 text-sm text-red-700">
              <p><strong>{t('charts.company', 'Company:')}:</strong> {companyData.company_info.nombre}</p>
              <p><strong>{t('charts.assessments', 'Assessments:')}:</strong> {companyData.company_info.total_assessments}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Multiple Employees Comparison Radar Chart
const MultipleEmployeesRadarChart = ({ employeesData, companyData, className = "" }) => {
  const { t } = useLanguage();

  if (!employeesData || employeesData.length === 0 || !companyData?.radar_chart) {
    return (
      <div className={`flex items-center justify-center bg-gray-50 rounded-lg ${className}`} style={{ minHeight: 400 }}>
        <p className="text-gray-500">
          {t('charts.select_employees', 'Select employees to compare')}
        </p>
      </div>
    );
  }

  // Generate distinct colors for each employee
  const generateColor = (index, opacity = 0.2) => {
    const colors = [
      [54, 162, 235],   // Blue
      [255, 99, 132],   // Red
      [255, 205, 86],   // Yellow
      [75, 192, 192],   // Teal
      [153, 102, 255],  // Purple
      [255, 159, 64],   // Orange
      [201, 203, 207],  // Grey
      [255, 99, 255],   // Pink
    ];
    
    const [r, g, b] = colors[index % colors.length];
    return {
      backgroundColor: `rgba(${r}, ${g}, ${b}, ${opacity})`,
      borderColor: `rgba(${r}, ${g}, ${b}, 0.8)`,
      pointBackgroundColor: `rgba(${r}, ${g}, ${b}, 0.8)`,
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: `rgba(${r}, ${g}, ${b}, 0.8)`
    };
  };

  // Combine all datasets
  const datasets = [];
  
  // Add company data as the first dataset (either average or profile)
  const isCompanyProfile = companyData.comparison_type === 'company_profile';
  
  const companyLabel = isCompanyProfile 
    ? t('charts.company_profile', 'Company Profile')
    : t('charts.company_average', 'Company Average');
  
  datasets.push({
    ...companyData.radar_chart.datasets[0],
    label: companyLabel,
    backgroundColor: isCompanyProfile ? 'rgba(34, 197, 94, 0.1)' : 'rgba(128, 128, 128, 0.1)',
    borderColor: isCompanyProfile ? 'rgba(34, 197, 94, 0.6)' : 'rgba(128, 128, 128, 0.6)',
    pointBackgroundColor: isCompanyProfile ? 'rgba(34, 197, 94, 0.6)' : 'rgba(128, 128, 128, 0.6)',
    borderWidth: 3,
    borderDash: [5, 5] // Dashed line for company
  });

  // Add each selected employee
  employeesData.forEach((empData, index) => {
    if (empData.radar_chart && empData.radar_chart.datasets[0]) {
      const colors = generateColor(index);
      datasets.push({
        ...empData.radar_chart.datasets[0],
        label: empData.assessment_info?.usuario_nombre || `Employee ${index + 1}`,
        ...colors,
        borderWidth: 2,
        tension: 0.1
      });
    }
  });

  const comparisonData = {
    labels: companyData.radar_chart.labels,
    datasets: datasets
  };

  const comparisonTitle = isCompanyProfile
    ? t('charts.multiple_vs_profile', 'Multiple Employees vs Company Profile')
    : t('charts.multiple_vs_average', 'Multiple Employees vs Company Average');

  return (
    <div className="space-y-6">
      <RadarChart
        data={comparisonData}
        title={comparisonTitle}
        type="comparison"
        width={700}
        height={500}
        className={className}
      />
      
      {/* Employee Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Company Average Card */}
        <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-300">
          <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
            <div className="w-4 h-4 border-2 border-gray-600 border-dashed rounded-full mr-2"></div>
            {t('charts.company_average', 'Company Average')}
          </h4>
          {companyData.company_info && (
            <div className="space-y-1 text-sm text-gray-600">
              <p><strong>{t('charts.assessments', 'Assessments:')}:</strong> {companyData.company_info.total_assessments}</p>
            </div>
          )}
        </div>

        {/* Individual Employee Cards */}
        {employeesData.map((empData, index) => {
          const colors = generateColor(index, 1);
          return (
            <div key={empData.employee_id} className="bg-white p-4 rounded-lg border-2" style={{ borderColor: colors.borderColor }}>
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                <div 
                  className="w-4 h-4 rounded-full mr-2 border-2 border-white" 
                  style={{ backgroundColor: colors.borderColor }}
                ></div>
                {empData.assessment_info?.usuario_nombre}
              </h4>
              {empData.assessment_info && (
                <div className="space-y-1 text-sm text-gray-600">
                  <p><strong>{t('charts.type', 'Type:')}:</strong> {empData.assessment_info.tipo}</p>
                </div>
              )}
              {empData.scores && (
                <div className="mt-2 grid grid-cols-3 gap-2">
                  <div className="text-center">
                    <p className="text-xs text-gray-500">{t('charts.comp_short', 'Comp.')}</p>
                    <p className="font-bold text-blue-600">{Math.round(empData.scores.compatibilidad)}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">{t('charts.bal_short', 'Bal.')}</p>
                    <p className="font-bold text-green-600">{Math.round(empData.scores.balance)}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500">{t('charts.growth_short', 'Growth')}</p>
                    <p className="font-bold text-purple-600">{Math.round(empData.scores.potencial_crecimiento)}%</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// New component for detailed comparison charts
const DetailedComparisonRadarChart = ({ chartData, className = "" }) => {
  const { t } = useLanguage();
  
  if (!chartData) {
    return (
      <div className={`p-8 text-center ${className}`}>
        <p className="text-gray-500">{t('charts.no_data', 'No chart data available')}</p>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div style={{ position: 'relative', height: '400px' }}>
        <Radar data={chartData.data} options={chartData.options} />
      </div>
    </div>
  );
};

export default RadarChart;
export { IndividualRadarChart, CompanyRadarChart, MultipleEmployeesRadarChart, ComparisonRadarChart, DetailedComparisonRadarChart };